import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SystemMember {
  id: string;
  name: string | null;
  email: string | null;
  phone_number: string | null;
  avatar_url: string | null;
  created_at: string;
  platform_role: 'super_admin' | 'admin' | 'member' | 'guest';
  organization_memberships: {
    org_id: string;
    org_name: string;
    org_subdomain: string;
    role: 'admin' | 'member' | 'guest';
    joined_at: string;
  }[];
  stats: {
    total_bookings: number;
    last_activity_date: string | null;
    last_organization_accessed: string | null;
    support_tickets_count: number;
  };
  insights: {
    activity_score: number;
    engagement_level: 'low' | 'medium' | 'high';
    is_multi_org: boolean;
    has_admin_roles: boolean;
    onboarding_complete: boolean;
    days_since_last_activity: number | null;
  };
}

interface UseSystemMembersOptions {
  page?: number;
  pageSize?: number;
}

export const useSystemMembers = (options: UseSystemMembersOptions = {}) => {
  const { page = 1, pageSize = 50 } = options;
  const offset = (page - 1) * pageSize;
  
  return useQuery({
    queryKey: ['system-members', page, pageSize],
    queryFn: async (): Promise<{ members: SystemMember[]; totalCount: number; totalPages: number }> => {
      // Get all profiles with their platform roles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          email,
          phone_number,
          avatar_url,
          created_at
        `);

      if (profilesError) throw profilesError;

      // Get platform roles separately
      const { data: platformRoles, error: platformRolesError } = await supabase
        .from('platform_user_roles')
        .select('user_id, role');

      if (platformRolesError) throw platformRolesError;

      // Get organization memberships with organizations data
      const { data: orgMemberships, error: orgError } = await supabase
        .from('organization_user_roles')
        .select(`
          user_id,
          org_id,
          role,
          created_at
        `);

      if (orgError) throw orgError;

      // Get organizations data separately
      const { data: organizations, error: orgsError } = await supabase
        .from('organizations')
        .select('id, name, subdomain');

      if (orgsError) throw orgsError;

      // Get booking statistics with id field
      const { data: bookingStats, error: bookingError } = await supabase
        .from('bookings')
        .select('id, user_id, created_at, org_id');

      if (bookingError) throw bookingError;

      // Get support ticket counts
      const { data: supportStats, error: supportError } = await supabase
        .from('support_tickets')
        .select('user_id, created_at');

      if (supportError) throw supportError;

      // Get activity logs for recent activity
      const { data: activityLogs, error: activityError } = await supabase
        .from('booking_activity_logs')
        .select('actor_id, created_at, booking_id')
        .order('created_at', { ascending: false });

      if (activityError) throw activityError;

      // Get booking user relationships for activity logs
      const { data: bookingsForLogs, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, user_id');

      if (bookingsError) throw bookingsError;

      // Process and combine data
      const processedMembers: SystemMember[] = profiles.map(profile => {
        // Platform role - map database role to our expected interface
        const userPlatformRole = platformRoles?.find(pr => pr.user_id === profile.id);
        const dbRole = userPlatformRole?.role || 'guest';
        const platformRole: 'super_admin' | 'admin' | 'member' | 'guest' = 
          dbRole === 'support' ? 'admin' : dbRole as 'super_admin' | 'admin' | 'member' | 'guest';

        // Organization memberships
        const userOrgMemberships = orgMemberships
          .filter(membership => membership.user_id === profile.id)
          .map(membership => {
            const org = organizations?.find(o => o.id === membership.org_id);
            return {
              org_id: membership.org_id,
              org_name: org?.name || 'Unknown',
              org_subdomain: org?.subdomain || '',
              role: membership.role,
              joined_at: membership.created_at
            };
          });

        // Booking statistics
        const userBookings = bookingStats.filter(booking => booking.user_id === profile.id);
        const totalBookings = userBookings.length;
        
        // Last activity from bookings or activity logs
        const userActivityFromBookings = userBookings.map(b => new Date(b.created_at));
        
        // Find activity logs that are either by this user or for their bookings
        const userBookingIds = userBookings.map(b => b.user_id === profile.id ? b.id : null).filter(Boolean);
        const userActivityFromLogs = activityLogs
          .filter(log => 
            log.actor_id === profile.id || 
            userBookingIds.includes(log.booking_id)
          )
          .map(log => new Date(log.created_at));
        
        const allActivities = [...userActivityFromBookings, ...userActivityFromLogs];
        const lastActivityDate = allActivities.length > 0 
          ? new Date(Math.max(...allActivities.map(d => d.getTime()))).toISOString()
          : null;

        // Last organization accessed (from most recent booking)
        const lastOrgAccessed = userBookings.length > 0
          ? (() => {
              const mostRecentBooking = userBookings
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
              const org = organizations?.find(o => o.id === mostRecentBooking.org_id);
              return org?.name || null;
            })()
          : null;

        // Support tickets count
        const supportTicketsCount = supportStats.filter(ticket => ticket.user_id === profile.id).length;

        // Calculate insights
        const isMultiOrg = userOrgMemberships.length > 1;
        const hasAdminRoles = userOrgMemberships.some(membership => membership.role === 'admin') || 
                              platformRole === 'admin' || platformRole === 'super_admin';

        // Activity score (bookings per month since joining)
        const memberSince = new Date(profile.created_at);
        const monthsSinceMember = Math.max(1, 
          (new Date().getTime() - memberSince.getTime()) / (1000 * 60 * 60 * 24 * 30)
        );
        const activityScore = Math.round((totalBookings / monthsSinceMember) * 10) / 10;

        // Engagement level
        let engagementLevel: 'low' | 'medium' | 'high' = 'low';
        if (activityScore >= 2) engagementLevel = 'high';
        else if (activityScore >= 0.5) engagementLevel = 'medium';

        // Days since last activity
        const daysSinceLastActivity = lastActivityDate
          ? Math.floor((new Date().getTime() - new Date(lastActivityDate).getTime()) / (1000 * 60 * 60 * 24))
          : null;

        // Onboarding complete check
        const onboardingComplete = !!(profile.name && profile.email && totalBookings > 0);

        return {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          phone_number: profile.phone_number,
          avatar_url: profile.avatar_url,
          created_at: profile.created_at,
          platform_role: platformRole,
          organization_memberships: userOrgMemberships,
          stats: {
            total_bookings: totalBookings,
            last_activity_date: lastActivityDate,
            last_organization_accessed: lastOrgAccessed,
            support_tickets_count: supportTicketsCount
          },
          insights: {
            activity_score: activityScore,
            engagement_level: engagementLevel,
            is_multi_org: isMultiOrg,
            has_admin_roles: hasAdminRoles,
            onboarding_complete: onboardingComplete,
            days_since_last_activity: daysSinceLastActivity
          }
        };
      });

      const sortedMembers = processedMembers.sort((a, b) => {
        // Sort by platform role priority, then by activity
        const rolePriority = { super_admin: 4, admin: 3, member: 2, guest: 1 };
        const aPriority = rolePriority[a.platform_role];
        const bPriority = rolePriority[b.platform_role];
        
        if (aPriority !== bPriority) return bPriority - aPriority;
        return b.stats.total_bookings - a.stats.total_bookings;
      });

      // Apply pagination
      const totalCount = sortedMembers.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const paginatedMembers = sortedMembers.slice(offset, offset + pageSize);

      return {
        members: paginatedMembers,
        totalCount,
        totalPages
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};